/**
 * Live inverse-dynamics solver. Loads @mujoco/mujoco (single-thread) on demand.
 * Does not step the simulation and does not write bone transforms.
 */
import { DEFAULT_HEIGHT_M, resolveMass } from "./constants.ts";
import {
  MUJOCO_MAX_PEOPLE,
  compileHumans,
  jointQuaternion,
  modelKey,
  standingPoints,
  type BuiltPerson,
  type MujocoPersonSpec,
} from "./mujocoMjcf.ts";
import {
  alignQuat,
  ballMomentWorld,
  contactWrenchWorld,
  freejointResidual,
  interactionForceWorld,
  momentLoad,
} from "./mujocoMap.ts";
import type {
  BiomechContact,
  BiomechFrame,
  BiomechFramePerson,
  BiomechJoint,
  CanonicalPoint,
  EstimatorPersonInput,
  JointId,
  PointMap,
  Vec3,
} from "./types.ts";

/** Wasm failed to load. `skip` lets the node test skip instead of failing. */
export class MujocoLoadError extends Error {
  readonly skip = true;
  constructor(message: string) {
    super(message);
    this.name = "MujocoLoadError";
  }
}

export interface MujocoSolveOptions {
  /** Paused / scrubbing / dragging: qvel = 0, qacc = 0. */
  held: boolean;
  floorY: number;
}

export interface StandingCheck {
  groundReactionY: number;
  residualY: number;
  residualF: Vec3;
  weight: number;
  footContacts: number;
  contactCount: number;
}

const MUJOCO_BROWSER_URL = "/js/human-scene/mujoco/mujoco.js";
const MIN_CUTOFF = 2.5;
const BETA = 0.7;
const D_CUTOFF = 1.0;

type WasmArray = { [index: number]: number; length: number };
interface WasmHandle {
  delete(): void;
}
interface WasmBuffer extends WasmHandle {
  GetView(): ArrayLike<number>;
}
interface WasmModel extends WasmHandle {
  nq: number;
  nv: number;
  jnt(name: string): WasmNamed & WasmHandle;
  geom(name: string): WasmNamed & WasmHandle;
}
interface WasmNamed {
  id: unknown;
  qposadr?: unknown;
  dofadr?: unknown;
}
interface WasmData extends WasmHandle {
  ncon: number;
  qpos: WasmArray;
  qvel: WasmArray;
  qacc: WasmArray;
  contact: {
    size(): number;
    get(index: number): WasmContact | undefined;
    delete(): void;
  };
  jnt(name: string): WasmSlice & WasmHandle;
  body(name: string): WasmBody & WasmHandle;
}
interface WasmSlice {
  qfrc_inverse: ArrayLike<number>;
}
interface WasmBody {
  xmat: ArrayLike<number>;
  xpos: ArrayLike<number>;
  cfrc_int: ArrayLike<number>;
  subtree_com: ArrayLike<number>;
}
interface WasmContact extends WasmHandle {
  geom?: ArrayLike<number>;
  geom1: number;
  geom2: number;
  pos: ArrayLike<number>;
  frame: ArrayLike<number>;
}
interface MujocoApi {
  MjModel: { from_xml_string(xml: string): WasmModel };
  MjData: new (model: WasmModel) => WasmData;
  DoubleBuffer: new (count: number) => WasmBuffer;
  mj_fwdPosition(model: WasmModel, data: WasmData): void;
  mj_inverse(model: WasmModel, data: WasmData): void;
  mj_rnePostConstraint(model: WasmModel, data: WasmData): void;
  mj_contactForce(model: WasmModel, data: WasmData, index: number, out: WasmBuffer): void;
  mj_differentiatePos(model: WasmModel, qvel: WasmBuffer, dt: number, qpos1: number[], qpos2: number[]): void;
}

interface Slot {
  name: string;
  kind: "free" | "ball";
  qposadr: number;
  nq: number;
}

interface LayerCache {
  key: string;
  model: WasmModel;
  data: WasmData;
  slots: Map<string, Slot>;
  geomById: Map<number, { personId: number; jointId: JointId }>;
  floorGeomId: number;
  prevQpos: number[] | null;
  prevQvel: number[] | null;
  prevT: number | null;
  prevQuat: Map<string, [number, number, number, number]>;
}

/** Same one-euro the realtime estimator uses (minCutoff 2.5, beta 0.7, dCutoff 1). */
class OneEuroScalar {
  private xPrev = 0;
  private dxPrev = 0;
  private tPrev = 0;
  private started = false;
  private readonly minCutoff: number;
  private readonly beta: number;
  private readonly dCutoff: number;

  constructor(minCutoff: number, beta: number, dCutoff: number) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
  }

  filter(t: number, x: number): number {
    if (!this.started) {
      this.started = true;
      this.tPrev = t;
      this.xPrev = x;
      this.dxPrev = 0;
      return x;
    }
    const dt = Math.max(1e-4, t - this.tPrev);
    this.tPrev = t;
    const dx = (x - this.xPrev) / dt;
    const dxHat = lowPass(dx, this.dxPrev, alpha(dt, this.dCutoff));
    this.dxPrev = dxHat;
    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const xHat = lowPass(x, this.xPrev, alpha(dt, cutoff));
    this.xPrev = xHat;
    return xHat;
  }
}

class OneEuroVec3 {
  private readonly f: OneEuroScalar[];
  constructor(minCutoff: number, beta: number, dCutoff: number) {
    this.f = [
      new OneEuroScalar(minCutoff, beta, dCutoff),
      new OneEuroScalar(minCutoff, beta, dCutoff),
      new OneEuroScalar(minCutoff, beta, dCutoff),
    ];
  }
  filter(t: number, p: Vec3): Vec3 {
    return [this.f[0]!.filter(t, p[0]), this.f[1]!.filter(t, p[1]), this.f[2]!.filter(t, p[2])];
  }
}

function alpha(dt: number, cutoff: number): number {
  const tau = 1 / (2 * Math.PI * Math.max(1e-6, cutoff));
  return 1 / (1 + tau / dt);
}

function lowPass(x: number, prev: number, a: number): number {
  return a * x + (1 - a) * prev;
}

function scalar(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "bigint") return Number(value);
  if (value != null && typeof value === "object" && "length" in value) {
    const list = value as ArrayLike<number>;
    if (list.length > 0) return Number(list[0]);
  }
  return Number(value);
}

function readVec(view: ArrayLike<number> | undefined, count: number): number[] {
  const out = new Array<number>(count);
  for (let i = 0; i < count; i++) out[i] = Number(view?.[i] ?? 0);
  return out;
}

function using<T extends WasmHandle, R>(obj: T, fn: (obj: T) => R): R {
  try {
    return fn(obj);
  } finally {
    obj.delete();
  }
}

function siteUrl(path: string): string {
  try {
    const base = typeof document !== "undefined" ? document.baseURI : undefined;
    return base ? new URL(path.replace(/^\//, ""), base).href : path;
  } catch {
    return path;
  }
}

/** Node tests import the package. The browser fetches the vendored single-thread build. */
function mujocoSpecifier(): string {
  if (typeof document === "undefined") return "@mujoco/" + "mujoco";
  return siteUrl(MUJOCO_BROWSER_URL);
}

function pointSpan(points: PointMap): number {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of Object.values(points)) {
    if (!p) continue;
    lo = Math.min(lo, p[1]);
    hi = Math.max(hi, p[1]);
  }
  const h = hi - lo;
  return Number.isFinite(h) && h > 0.5 ? h : 0;
}

function specOf(person: EstimatorPersonInput): MujocoPersonSpec {
  const heightM =
    person.heightM != null && person.heightM > 0.5
      ? person.heightM
      : pointSpan(person.points) > 0.5
        ? pointSpan(person.points) * 1.05
        : null;
  return {
    id: person.id,
    massKg: resolveMass(person.massKg, heightM).massKg,
    heightM: heightM ?? DEFAULT_HEIGHT_M,
    points: person.points,
  };
}

function contactPair(contact: WasmContact): [number, number] {
  const geom = contact.geom;
  if (geom && typeof geom.length === "number" && geom.length >= 2) {
    const g0 = Number(geom[0]);
    const g1 = Number(geom[1]);
    if (Number.isFinite(g0) && Number.isFinite(g1)) return [g0, g1];
  }
  return [Number(contact.geom1), Number(contact.geom2)];
}

export class MujocoLiveSolver {
  private api: MujocoApi | null = null;
  private loading: Promise<void> | null = null;
  private loadError: MujocoLoadError | null = null;
  private readonly layers = new Map<string, LayerCache>();
  private readonly filters = new Map<string, Map<string, OneEuroVec3>>();
  private readonly filterTime = new Map<string, number>();
  /** Body masses from the latest solves. The caller clears this once per frame. */
  readonly lastMasses = new Map<number, number>();

  get ready(): boolean {
    return this.api != null;
  }

  get failed(): boolean {
    return this.loadError != null;
  }

  /** Start (or return) the wasm load. A failed load is not retried until `retry()`. */
  ensureLoaded(): Promise<void> {
    if (this.api) return Promise.resolve();
    if (this.loadError) return Promise.reject(this.loadError);
    if (!this.loading) {
      this.loading = this.load().then(
        (api) => {
          this.api = api;
        },
        (err: unknown) => {
          this.loadError =
            err instanceof MujocoLoadError
              ? err
              : new MujocoLoadError(`skip: ${err instanceof Error ? err.message : String(err)}`);
          this.loading = null;
          throw this.loadError;
        },
      );
    }
    return this.loading;
  }

  /** Allow another load attempt after the user selects MuJoCo again. */
  retry(): void {
    if (this.api || !this.loadError) return;
    this.loadError = null;
    this.loading = null;
  }

  /**
   * Inverse dynamics for one compare layer. Throws if wasm is not loaded or the step fails.
   * Returns null when nobody in the layer has a usable trunk.
   */
  solve(layer: string, t: number, persons: EstimatorPersonInput[], opts: MujocoSolveOptions): BiomechFrame | null {
    const api = this.api;
    if (!api) throw new Error("mujoco: not loaded");
    if (persons.length > MUJOCO_MAX_PEOPLE) {
      throw new Error(`mujoco: at most ${MUJOCO_MAX_PEOPLE} people`);
    }
    const time = Number.isFinite(t) ? t : 0;
    let cache = this.layers.get(layer);
    if (cache && cache.prevT != null && time < cache.prevT - 1e-6) {
      cache.prevQpos = null;
      cache.prevQvel = null;
      cache.prevQuat.clear();
      cache.prevT = null;
      this.filters.delete(layer);
      this.filterTime.delete(layer);
    }
    const samples = this.smoothPoints(layer, time, persons, opts.held);
    const built = compileHumans(samples.map(specOf), opts.floorY);
    if (built.people.length === 0) return null;
    const key = modelKey(built.people, opts.floorY);
    if (!cache || cache.key !== key) cache = this.rebuild(layer, built, key);
    this.writeQpos(cache, built.people);
    api.mj_fwdPosition(cache.model, cache.data);
    this.applyDerivatives(api, cache, time, opts.held);
    api.mj_inverse(cache.model, cache.data);
    api.mj_rnePostConstraint(cache.model, cache.data);
    return this.toFrame(cache, built.people, time);
  }

  /** One held standing pose. Load errors propagate as `MujocoLoadError` (skip). */
  async solveStandingCheck(): Promise<StandingCheck> {
    await this.ensureLoaded();
    const massKg = 70;
    const heightM = 1.72;
    const frame = this.solve(
      "standing",
      0,
      [{ id: 0, massKg, heightM, points: standingPoints(heightM) }],
      { held: true, floorY: 0 },
    );
    const person = frame?.persons[0];
    if (!person) throw new Error("mujoco: standing pose produced no person");
    let groundReactionY = 0;
    let footContacts = 0;
    for (const contact of frame?.contacts ?? []) {
      if (contact.kind !== "ground") continue;
      if (contact.joint !== "ankle_l" && contact.joint !== "ankle_r") continue;
      footContacts += 1;
      groundReactionY += contact.f[1];
    }
    return {
      groundReactionY,
      residualY: person.residual.f[1],
      residualF: person.residual.f,
      weight: massKg * 9.81,
      footContacts,
      contactCount: frame?.contacts.length ?? 0,
    };
  }

  /** Drop compiled models and filter history. The wasm module stays loaded. */
  reset(): void {
    for (const cache of this.layers.values()) this.release(cache);
    this.layers.clear();
    this.filters.clear();
    this.filterTime.clear();
    this.lastMasses.clear();
  }

  dispose(): void {
    this.reset();
  }

  private async load(): Promise<MujocoApi> {
    const spec = mujocoSpecifier();
    try {
      const mod = await import(/* webpackIgnore: true */ spec);
      const factory = mod.default as (() => Promise<MujocoApi>) | undefined;
      if (typeof factory !== "function") throw new Error("mujoco module has no loader");
      const api = await factory();
      if (!api?.MjModel?.from_xml_string || typeof api.DoubleBuffer !== "function") {
        throw new Error("mujoco loader returned no API");
      }
      return api;
    } catch (err) {
      if (err instanceof MujocoLoadError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      throw new MujocoLoadError(`skip: mujoco wasm failed to load (${message})`);
    }
  }

  private smoothPoints(
    layer: string,
    t: number,
    persons: EstimatorPersonInput[],
    held: boolean,
  ): EstimatorPersonInput[] {
    if (held) return persons;
    const last = this.filterTime.get(layer);
    if (last != null && t < last - 1e-6) this.filters.delete(layer);
    this.filterTime.set(layer, t);
    let bag = this.filters.get(layer);
    if (!bag) {
      bag = new Map();
      this.filters.set(layer, bag);
    }
    const seen = new Set<string>();
    const out: EstimatorPersonInput[] = [];
    for (const person of persons) {
      const points: PointMap = {};
      for (const [name, p] of Object.entries(person.points)) {
        if (!p) continue;
        const key = `${person.id}:${name}`;
        seen.add(key);
        let filter = bag.get(key);
        if (!filter) {
          filter = new OneEuroVec3(MIN_CUTOFF, BETA, D_CUTOFF);
          bag.set(key, filter);
        }
        points[name as CanonicalPoint] = filter.filter(t, p as Vec3);
      }
      out.push({ ...person, points });
    }
    for (const key of bag.keys()) {
      if (!seen.has(key)) bag.delete(key);
    }
    return out;
  }

  private rebuild(layer: string, built: { xml: string; people: BuiltPerson[] }, key: string): LayerCache {
    const api = this.api;
    if (!api) throw new Error("mujoco: not loaded");
    const prev = this.layers.get(layer);
    if (prev) {
      this.layers.delete(layer);
      this.release(prev);
    }
    const model = api.MjModel.from_xml_string(built.xml);
    if (!model) throw new Error("mujoco: MJCF compile failed");
    let data: WasmData | null = null;
    try {
      data = new api.MjData(model);
      const cache = this.bind(model, data, built.people, key);
      this.layers.set(layer, cache);
      return cache;
    } catch (err) {
      data?.delete();
      model.delete();
      throw err;
    }
  }

  private bind(model: WasmModel, data: WasmData, people: BuiltPerson[], key: string): LayerCache {
    let nq = 0;
    let nv = 0;
    for (const person of people) {
      for (const joint of person.joints) {
        nq += joint.kind === "free" ? 7 : 4;
        nv += joint.kind === "free" ? 6 : 3;
      }
    }
    if (model.nq !== nq || model.nv !== nv) {
      throw new Error(`mujoco: model size nq ${model.nq} nv ${model.nv}, expected ${nq}/${nv}`);
    }
    const slots = new Map<string, Slot>();
    for (const person of people) {
      for (const joint of person.joints) {
        const slot = using(model.jnt(joint.name), (acc) => {
          const qposadr = scalar(acc.qposadr);
          if (!Number.isFinite(qposadr)) throw new Error(`mujoco: bad qpos address for ${joint.name}`);
          return {
            name: joint.name,
            kind: joint.kind,
            qposadr,
            nq: joint.kind === "free" ? 7 : 4,
          };
        });
        slots.set(joint.name, slot);
      }
    }
    const geomById = new Map<number, { personId: number; jointId: JointId }>();
    for (const person of people) {
      for (const geom of person.geoms) {
        const id = using(model.geom(geom.name), (acc) => scalar(acc.id));
        if (!Number.isFinite(id)) throw new Error(`mujoco: bad geom id for ${geom.name}`);
        geomById.set(id, { personId: person.id, jointId: geom.jointId });
      }
    }
    const floorGeomId = using(model.geom("floor"), (acc) => scalar(acc.id));
    if (!Number.isFinite(floorGeomId)) throw new Error("mujoco: floor geom missing");
    return {
      key,
      model,
      data,
      slots,
      geomById,
      floorGeomId,
      prevQpos: null,
      prevQvel: null,
      prevT: null,
      prevQuat: new Map(),
    };
  }

  private writeQpos(cache: LayerCache, people: BuiltPerson[]): void {
    const qpos = cache.data.qpos;
    for (const person of people) {
      const poseOf = new Map(person.poses.map((pose) => [pose.segment, pose]));
      for (const joint of person.joints) {
        const slot = cache.slots.get(joint.name);
        const pose = poseOf.get(joint.segment);
        if (!slot || !pose) throw new Error(`mujoco: missing joint ${joint.name}`);
        const parentPose = pose.parent ? poseOf.get(pose.parent) ?? null : null;
        const q = alignQuat(jointQuaternion(pose, parentPose), cache.prevQuat.get(joint.name));
        cache.prevQuat.set(joint.name, q);
        const adr = slot.qposadr;
        if (slot.kind === "free") {
          qpos[adr] = pose.origin[0];
          qpos[adr + 1] = pose.origin[1];
          qpos[adr + 2] = pose.origin[2];
          qpos[adr + 3] = q[0];
          qpos[adr + 4] = q[1];
          qpos[adr + 5] = q[2];
          qpos[adr + 6] = q[3];
        } else {
          qpos[adr] = q[0];
          qpos[adr + 1] = q[1];
          qpos[adr + 2] = q[2];
          qpos[adr + 3] = q[3];
        }
      }
    }
  }

  private applyDerivatives(api: MujocoApi, cache: LayerCache, t: number, held: boolean): void {
    const nv = cache.model.nv;
    const qpos = readVec(cache.data.qpos, cache.model.nq);
    const qvel = new Array<number>(nv).fill(0);
    const qacc = new Array<number>(nv).fill(0);
    const canDiff = !held && cache.prevQpos != null && cache.prevT != null && t > cache.prevT + 1e-6;
    if (canDiff) {
      const dt = Math.max(1e-4, t - cache.prevT!);
      const buf = new api.DoubleBuffer(nv);
      try {
        api.mj_differentiatePos(cache.model, buf, dt, cache.prevQpos!, qpos);
        const view = buf.GetView();
        for (let i = 0; i < nv; i++) qvel[i] = Number(view[i] ?? 0);
      } finally {
        buf.delete();
      }
      if (cache.prevQvel) {
        for (let i = 0; i < nv; i++) qacc[i] = (qvel[i]! - cache.prevQvel[i]!) / dt;
      }
      cache.prevQvel = qvel.slice();
    } else {
      cache.prevQvel = null;
    }
    for (let i = 0; i < nv; i++) {
      cache.data.qvel[i] = qvel[i]!;
      cache.data.qacc[i] = qacc[i]!;
    }
    cache.prevQpos = qpos;
    cache.prevT = t;
  }

  private toFrame(cache: LayerCache, people: BuiltPerson[], t: number): BiomechFrame {
    const persons: BiomechFramePerson[] = [];
    for (const person of people) {
      const poseOf = new Map(person.poses.map((pose) => [pose.segment, pose]));
      const joints: Partial<Record<JointId, BiomechJoint>> = {};
      for (const joint of person.joints) {
        if (joint.kind !== "ball" || !joint.jointId) continue;
        const pose = poseOf.get(joint.segment);
        if (!pose) continue;
        const jointId = joint.jointId;
        const qfrc = using(cache.data.jnt(joint.name), (acc) => readVec(acc.qfrc_inverse, 3));
        const body = using(cache.data.body(`p${person.id}_${joint.segment}`), (acc) => ({
          xmat: readVec(acc.xmat, 9),
          cfrc: readVec(acc.cfrc_int, 6),
        }));
        const m = ballMomentWorld(qfrc as Vec3, body.xmat);
        joints[jointId] = {
          p: [pose.origin[0], pose.origin[1], pose.origin[2]],
          f: interactionForceWorld(body.cfrc),
          m,
          load: momentLoad(jointId, m, person.massKg),
        };
      }
      const rootName = `p${person.id}_root`;
      const qfrc = using(cache.data.jnt(rootName), (acc) => readVec(acc.qfrc_inverse, 6));
      const trunk = using(cache.data.body(`p${person.id}_trunk`), (acc) => ({
        xmat: readVec(acc.xmat, 9),
        xpos: readVec(acc.xpos, 3) as Vec3,
        com: readVec(acc.subtree_com, 3) as Vec3,
      }));
      this.lastMasses.set(person.id, person.massKg);
      persons.push({
        id: person.id,
        com: trunk.com,
        joints,
        residual: freejointResidual(qfrc, trunk.xmat, trunk.xpos, trunk.com),
      });
    }
    return { t, persons, contacts: this.readContacts(cache) };
  }

  private readContacts(cache: LayerCache): BiomechContact[] {
    const api = this.api;
    if (!api) return [];
    const n = Number(cache.data.ncon) || 0;
    if (n <= 0) return [];
    const list = cache.data.contact;
    const out: BiomechContact[] = [];
    try {
      for (let i = 0; i < n; i++) {
        const contact = list.get(i);
        if (!contact) continue;
        const buf = new api.DoubleBuffer(6);
        try {
          api.mj_contactForce(cache.model, cache.data, i, buf);
          const force = readVec(buf.GetView(), 6);
          const [g0, g1] = contactPair(contact);
          const frame = readVec(contact.frame, 9);
          const pos = readVec(contact.pos, 3) as Vec3;
          const info0 = cache.geomById.get(g0);
          const info1 = cache.geomById.get(g1);
          const onFloor = g0 === cache.floorGeomId || g1 === cache.floorGeomId;
          let owner = info1;
          let geomIndex: 0 | 1 = 1;
          if (!owner && info0) {
            owner = info0;
            geomIndex = 0;
          }
          if (!owner) continue;
          const otherInfo = owner === info1 ? info0 : info1;
          const wrench = contactWrenchWorld(force, frame, geomIndex);
          if (Math.hypot(wrench.f[0], wrench.f[1], wrench.f[2]) < 1e-3) continue;
          const otherId = otherInfo && otherInfo.personId !== owner.personId ? otherInfo.personId : null;
          out.push({
            personId: owner.personId,
            otherPersonId: onFloor ? null : otherId,
            joint: owner.jointId,
            kind: onFloor ? "ground" : "person",
            p: pos,
            f: wrench.f,
          });
        } finally {
          buf.delete();
          contact.delete();
        }
      }
    } finally {
      list.delete();
    }
    return out;
  }

  private release(cache: LayerCache): void {
    try {
      cache.data.delete();
    } catch {
      /* already released */
    }
    try {
      cache.model.delete();
    } catch {
      /* already released */
    }
  }
}
